import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/** Aviso por correo que el sistema le mando al usuario en sesion. */
export interface AvisoCorreo {
  id: number;
  clave: string;
  nombre: string | null;
  cta_label: string | null;
  entidad_tipo: string | null;
  entidad_id: string | null;
  payload: Record<string, unknown> | null;
  estado: string | null;
  fecha: string;
}

export const CLAVE_AVISOS_CORREO_LEIDOS = 'admin:avisos-correo-leidos';

interface EstadoLeidos {
  ids: number[];
  marcarVarios: (ids: number[]) => void;
}

/** "Leido" vive en el navegador (igual que useNotificacionesLeidas del Legal Flow). */
export const useAvisosCorreoLeidosStore = create<EstadoLeidos>()(
  persist(
    (set) => ({
      ids: [],
      marcarVarios: (ids) =>
        set((s) => ({ ids: Array.from(new Set([...s.ids, ...ids])).slice(-500) })),
    }),
    { name: CLAVE_AVISOS_CORREO_LEIDOS },
  ),
);

const CINCO_MIN = 5 * 60 * 1000;

export function useMisAvisosCorreo(limite = 30) {
  const { user } = useAuth();
  const ids = useAvisosCorreoLeidosStore((s) => s.ids);
  const marcarVarios = useAvisosCorreoLeidosStore((s) => s.marcarVarios);

  const query = useQuery({
    queryKey: ['mis-avisos-correo', user?.id ?? null, limite],
    enabled: !!user,
    staleTime: CINCO_MIN,
    refetchInterval: CINCO_MIN,
    queryFn: async (): Promise<AvisoCorreo[]> => {
      const { data, error } = await (supabase as any).rpc('get_mis_avisos_correo', {
        p_limite: limite,
      });
      if (error) {
        // RPC aun no desplegada en este ambiente: se muestra el vacio normal.
        const msg = `${error.code ?? ''} ${error.message ?? ''}`;
        if (/PGRST202|42883|does not exist|Could not find the function/i.test(msg)) return [];
        throw error;
      }
      return (data ?? []) as AvisoCorreo[];
    },
  });

  const avisos = query.data ?? [];
  const leidas = new Set(ids);
  const sinLeer = avisos.filter((a) => !leidas.has(a.id)).length;

  return {
    ...query,
    avisos,
    sinLeer,
    estaLeido: (id: number) => leidas.has(id),
    marcarLeido: (id: number) => marcarVarios([id]),
    marcarTodos: () => marcarVarios(avisos.map((a) => a.id)),
  };
}
